import { defaultStackParser, makeFetchTransport } from "@sentry/browser";
import { debug, getCurrentScope, setCurrentClient, startInactiveSpan } from "@sentry/core";
import { Client, Scope, integrations, nativePlatform, platformOS } from "./SentryDependencies";
import { activeClientRegistry } from "./activeClientRegistry";
import { setupJsIntegrations } from "./setupJsIntegrations";
// This configuration is obtained from
// https://docs.sentry.io/clients/javascript/tips/#decluttering-sentry
const IGNORED_ERRORS = [
    // Random plugins/extensions
    "top.GLOBALS",
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    "originalCreateNotification",
    "canvas.contentDocument",
    "MyApp_RemoveAllHighlights",
    "http://tt.epicplay.com",
    "Can't find variable: ZiteReader",
    "jigsaw is not defined",
    "ComboSearch is not defined",
    "http://loading.retry.widdit.com/",
    "atomicFindClose",
    // Facebook borked
    "fb_xd_fragment",
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    "bmi_SafeAddOnload",
    "EBCallBackMessageReceived",
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    "conduitPage",
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    "Script error.",
    // Avast extension error
    "_avast_submit",
];
// Shared by the client options and the integrations factory: the react-native default integrations
// gate on `tracesSampleRate` being a number, so the two must not drift apart.
const TRACES_SAMPLE_RATE = 0.5;
const DENIED_URLS = [
    /kameleoon\.js/i,
    /kameleoon\.eu/i,
    /data\.kameleoon\.io/i,
    // TikTok
    /analytics\.tiktok\.com/i,
    /i18n\/pixel\//i,
    // Google Adsense
    /pagead\/js/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
];
// Built-in noise filter, always applied before any consumer `beforeSend`.
const applyBuiltInFilters = (event, hint) => {
    const message = event.message || hint?.originalException?.toString() || event.exception?.values?.[0]?.value || "";
    if (/kameleoon\.js|kameleoon\.eu|data\.kameleoon\.io/i.test(message) ||
        /analytics\.tiktok\.com/i.test(message) ||
        /snapchat/i.test(event.request?.headers?.["User-Agent"] ?? "")) {
        return null;
    }
    return event;
};
const sentryLogger = (environment, { role = "screen", isHost = false } = {}) => {
    let client;
    let scope;
    let currentCustomerId;
    let unregister;
    // Only the native owner enables the native SDK; non-owners build native-disabled and still deliver
    // JS events through their own fetch transport.
    const buildClient = (claimsNativeOwnership) => {
        const { environment: clientEnvironment, release, publicKey, project, dist = platformOS, enableIntegrations = true, routerTracing = true, allowUrls, ignoredErrors, beforeSend: consumerBeforeSend, debug: debugEnabled, } = environment();
        if (!(clientEnvironment && release && publicKey && project)) {
            // Without a built client every capture (boundaries, global handlers, manual) becomes a no-op.
            debug.warn("[logging] Sentry client not built: missing environment, release, publicKey or project.");
            return undefined;
        }
        const ownsNativeSdk = nativePlatform && claimsNativeOwnership;
        return new Client({
            environment: clientEnvironment,
            dist,
            release,
            debug: debugEnabled,
            dsn: `https://${publicKey}@o179049.ingest.sentry.io/${project}`,
            integrations: enableIntegrations
                ? integrations({
                    enableNative: ownsNativeSdk,
                    tracesSampleRate: TRACES_SAMPLE_RATE,
                    enableAutoPerformanceTracing: true,
                    enableAppStartTracking: true,
                    enableNativeFramesTracking: true,
                    enableStallTracking: true,
                    routerTracing,
                })
                : [],
            tracesSampleRate: TRACES_SAMPLE_RATE,
            // The RN `AppStart` integration reads this off `client.getOptions()` in its own `setup` and
            // self-disables when falsy — passing it to the integrations factory alone is not enough.
            // @ts-ignore
            enableAppStartTracking: true,
            // @ts-ignore
            enableNative: ownsNativeSdk,
            autoInitializeNativeSdk: ownsNativeSdk,
            stackParser: defaultStackParser,
            transport: makeFetchTransport,
            ignoreErrors: [...IGNORED_ERRORS, ...(ignoredErrors ?? [])],
            denyUrls: DENIED_URLS,
            allowUrls,
            async beforeSend(event, hint) {
                const filtered = applyBuiltInFilters(event, hint);
                if (filtered === null) {
                    return null;
                }
                return consumerBeforeSend ? consumerBeforeSend(filtered, hint) : filtered;
            },
        });
    };
    const bindClient = (newClient) => {
        scope ??= new Scope();
        scope.setUser({ id: currentCustomerId });
        scope.setClient(newClient);
    };
    /**
     * Build-once activation; a second call is a no-op.
     *
     * A non-owner native logger must NEVER call its own `client.init()`: on `@sentry/react-native` that
     * flips the shared, module-level `NATIVE.enableNative` flag and disables the owner's native binding.
     * It wires integrations through `setupJsIntegrations` instead.
     *
     * The client must be bound as the global current one BEFORE init: integration `afterAllSetup` hooks
     * gate on `getClient() === client`, so binding afterwards silently suppresses all of them.
     */
    const activate = ({ claimsNativeOwnership, becomesCurrent }) => {
        if (client) {
            return;
        }
        client = buildClient(claimsNativeOwnership);
        if (!client) {
            return;
        }
        bindClient(client);
        if (becomesCurrent) {
            setAsCurrent();
        }
        if (!nativePlatform || claimsNativeOwnership) {
            client.init();
        }
        else {
            setupJsIntegrations(client);
        }
    };
    // Binds this app as the global current client + user, so breadcrumbs and tracing — which Sentry
    // resolves through the current client, not the per-app `scope` — follow the active app.
    const setAsCurrent = () => {
        if (!client) {
            return;
        }
        setCurrentClient(client);
        getCurrentScope().setUser({ id: currentCustomerId });
    };
    const canCapture = () => scope !== undefined;
    // DSN project id of the built client; lets the registry expose which app is active.
    const projectId = () => client?.getDsn()?.projectId;
    const flush = (timeout) => client?.flush(timeout) ?? Promise.resolve(false);
    // `scope` is undefined only when no client was ever built; "" keeps the return type `string`.
    const captureException = (error, hint, tags) => {
        if (!scope) {
            return "";
        }
        if (!tags?.layer && !tags?.section) {
            return scope.captureException(error, hint);
        }
        // Clone so per-capture tags don't leak into the persistent scope.
        const tagged = scope.clone();
        if (tags.layer) {
            tagged.setTag("layer", tags.layer);
        }
        if (tags.section) {
            tagged.setTag("section", tags.section);
        }
        return tagged.captureException(error, hint);
    };
    const setContext = (key, context) => {
        scope?.setContext(key, context);
    };
    const onMount = (customerId) => {
        currentCustomerId = customerId;
        scope?.setUser({ id: customerId });
        // Guard against a double mount without an intervening unmount: never leak a stale stack entry.
        unregister?.();
        unregister = activeClientRegistry.register({
            activate,
            captureException,
            canCapture,
            flush,
            setAsCurrent,
            projectId,
        }, { role, isHost });
    };
    const onUnmount = () => {
        unregister?.();
        unregister = undefined;
        scope?.setUser(null);
    };
    /**
     * Activates WITHOUT taking the global current binding: `init()` leaves no registry stack entry, so
     * a binding taken here could never be handed back. Prefer `onMount`, which does both.
     */
    const init = () => {
        activate({ claimsNativeOwnership: role === "screen", becomesCurrent: false });
    };
    const startNavigationSpan = ({ name, attributes, startTimeSeconds, endTimeSeconds, }) => {
        if (!scope) {
            return;
        }
        const span = startInactiveSpan({
            name,
            op: "navigation",
            scope,
            forceTransaction: true,
            startTime: startTimeSeconds,
            attributes,
        });
        span.end(endTimeSeconds);
    };
    return {
        init,
        onMount,
        onUnmount,
        captureException,
        setContext,
        startNavigationSpan,
        getSentryClient: () => client,
    };
};
export { sentryLogger };
